import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { CustomEase } from 'gsap/CustomEase';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase);

/*
 * Motion de RBSIS. Principios que guían cada pieza:
 * - Una sola familia de curvas (fluid / swift), igual que en app.css.
 * - Acción superpuesta: nada arranca en bloque, todo escalona.
 * - Continuidad: los elementos se transforman entre estados en vez de aparecer y desaparecer.
 * - Puesta en escena: un solo foco por vez (al hover, el resto se apaga).
 * - Seguimiento: lo que sigue al cursor o al scroll llega con retraso y se inclina por la inercia.
 */
CustomEase.create('fluid', '0.32, 0.72, 0, 1');
CustomEase.create('swift', '0.76, 0, 0.24, 1');

const motion = () => document.documentElement.classList.contains('motion');
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

let lenis = null;

// Mientras las partículas viajan a la cabecera, la cabecera no se esconde (es el destino).
let docking = false;
// Con el menú móvil abierto la cabecera no se esconde.
let menuOpen = false;

/* ---------- Scroll suave sincronizado con ScrollTrigger ---------- */
function initSmoothScroll() {
    if (!motion()) return;
    lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 0.9 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Anclas de esta misma página, también las escritas con la URL completa (las del menú).
    document.querySelectorAll('a[href*="#"]').forEach((a) => {
        const url = new URL(a.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash) return;
        a.addEventListener('click', (e) => {
            const target = document.querySelector(url.hash);
            if (!target) return;
            e.preventDefault();
            lenis.scrollTo(target, { offset: -96, duration: 1.4 });
        });
    });
}

/* ---------- Telón entre páginas (continuidad: el logo viaja de una página a la otra) ---------- */
function openCurtain() {
    const curtain = document.querySelector('[data-curtain]');
    if (!curtain || !motion()) return Promise.resolve();
    const logo = curtain.querySelector('[data-curtain-logo]');

    return new Promise((resolve) => {
        gsap.timeline({ onComplete: resolve })
            .fromTo(logo, { yPercent: 0, autoAlpha: 1, filter: 'blur(0px)' }, { yPercent: -60, autoAlpha: 0, filter: 'blur(8px)', duration: 0.6, ease: 'swift' })
            .fromTo(curtain, { clipPath: 'inset(0% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.9, ease: 'swift' }, 0.15);
    });
}

function closeCurtainAndGo(href) {
    const curtain = document.querySelector('[data-curtain]');
    const logo = curtain.querySelector('[data-curtain-logo]');
    gsap.timeline({ onComplete: () => { window.location.href = href; } })
        .fromTo(curtain, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.75, ease: 'swift' })
        .fromTo(logo, { yPercent: 60, autoAlpha: 0, filter: 'blur(8px)' }, { yPercent: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 0.5, ease: 'fluid' }, 0.35);
}

function initPageTransitions() {
    if (!motion()) return;

    document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]');
        if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        if (a.target === '_blank' || a.hasAttribute('download')) return;
        const url = new URL(a.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname.startsWith('/admin')) return;
        if (url.pathname === window.location.pathname && url.hash) return;
        e.preventDefault();
        closeCurtainAndGo(url.href);
    });

    // Al volver con el botón "atrás", la página sale del caché con el telón cerrado.
    window.addEventListener('pageshow', (e) => {
        if (e.persisted) gsap.set('[data-curtain]', { clipPath: 'inset(0% 0% 100% 0%)' });
    });
}

/* ---------- Navegación: se esconde al bajar, vuelve al subir ---------- */
function initNav() {
    const nav = document.querySelector('[data-nav]');
    if (!nav || !motion()) return;
    const hero = document.querySelector('[data-hero]');
    // Al volver a su lugar se borra el transform: una cabecera con transform pasa a ser el contenedor
    // de sus hijos `fixed`, y el menú móvil (que vive adentro) quedaba del alto de la cabecera.
    const show = () => gsap.to(nav, { yPercent: 0, duration: 0.7, ease: 'fluid', overwrite: true, clearProps: 'transform' });
    ScrollTrigger.create({
        start: hero ? () => hero.offsetHeight * 0.6 : 160,
        onUpdate: (self) => {
            // Sin velocidad no hubo scroll: es un recálculo (recarga a mitad de página, cambio de tamaño). La cabecera se queda.
            if (menuOpen || self.getVelocity() === 0) return;
            self.direction === 1 && !docking
                ? gsap.to(nav, { yPercent: -110, duration: 0.7, ease: 'fluid', overwrite: true })
                : show();
        },
        onLeaveBack: show,
    });
}

/* ---------- Menú móvil: panel con clip-path, links en cascada ---------- */
function initMobileMenu() {
    const panel = document.querySelector('[data-mobile-menu]');
    if (!panel) return;
    const nav = document.querySelector('[data-nav]');
    window.addEventListener('menu:toggle', (e) => {
        const open = e.detail;
        menuOpen = open;
        open ? lenis?.stop() : lenis?.start();
        // El panel es `fixed inset-0` dentro de la cabecera: sin transform en ella, ocupa toda la pantalla.
        if (open) {
            gsap.killTweensOf(nav);
            gsap.set(nav, { clearProps: 'transform' });
        }
        if (!open || !motion()) return;
        requestAnimationFrame(() => {
            gsap.timeline()
                .fromTo(panel, { clipPath: 'inset(0% 0% 100% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.8, ease: 'swift' })
                .fromTo(panel.querySelectorAll('[data-menu-link]'), { yPercent: 110 }, { yPercent: 0, duration: 0.9, ease: 'fluid', stagger: 0.06 }, 0.25);
        });
    });
}

/* ---------- Botones magnéticos: atraen al cursor y vuelven con inercia ---------- */
function initMagnetic() {
    if (!motion() || !finePointer()) return;
    document.querySelectorAll('[data-magnetic]').forEach((el) => {
        const x = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'fluid' });
        const y = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'fluid' });
        el.addEventListener('pointermove', (e) => {
            const r = el.getBoundingClientRect();
            x((e.clientX - (r.left + r.width / 2)) * 0.35);
            y((e.clientY - (r.top + r.height / 2)) * 0.35);
        });
        el.addEventListener('pointerleave', () => { x(0); y(0); });
    });
}

/*
 * Títulos que no se cortan: si la palabra más larga no entra en el ancho disponible,
 * se reduce el tamaño lo justo. Evita que la máscara de la animación recorte letras.
 */
function fitLongestWord(el) {
    el.style.fontSize = '';
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;left:0;top:0';
    el.appendChild(probe);
    let widest = 0;
    for (const word of el.textContent.trim().split(/\s+/)) {
        probe.textContent = word;
        widest = Math.max(widest, probe.getBoundingClientRect().width);
    }
    probe.remove();
    const available = el.clientWidth;
    if (widest > available) {
        const size = parseFloat(getComputedStyle(el).fontSize);
        el.style.fontSize = `${Math.floor((size * available) / widest * 0.98)}px`;
    }
}

/*
 * Degradé continuo sobre palabras sueltas: cada palabra pinta su porción del degradé del párrafo
 * (así se puede animar palabra por palabra sin cortar el color). Las palabras dentro de [data-flag]
 * llevan los colores de la bandera argentina.
 */
const BRAND_GRADIENT = 'linear-gradient(135deg, #ebb464 0%, #a50339 100%)';
// Bandera: núcleo dorado del sol en el centro de la palabra, sobre las franjas celeste y blanca.
const FLAG_GRADIENT = 'radial-gradient(circle 0.42em at 50% 54%, #fff3cf 0%, #ebb464 38%, rgb(235 180 100 / 0) 100%), linear-gradient(180deg, #74acdf 18%, #ffffff 50%, #74acdf 82%)';

function paintWords(el, words) {
    const box = el.getBoundingClientRect();
    el.style.backgroundImage = 'none';
    el.querySelectorAll('[data-flag]').forEach((flag) => { flag.style.backgroundImage = 'none'; });
    words.forEach((word) => {
        const flag = word.closest('[data-flag]');
        const r = word.getBoundingClientRect();
        Object.assign(word.style, {
            backgroundImage: flag ? FLAG_GRADIENT : BRAND_GRADIENT,
            backgroundSize: flag ? 'auto, 100% 100%' : `${box.width}px ${box.height}px`,
            backgroundPosition: flag ? '0 0' : `${box.left - r.left}px ${box.top - r.top}px`,
            backgroundClip: 'text',
            webkitBackgroundClip: 'text',
            color: 'transparent',
        });
    });
}

/* ---------- Revelados de texto ---------- */
function initReveals() {
    const reduced = !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    if (reduced) {
        gsap.set(['[data-split]', '[data-reveal]', '[data-reveal-group] > *'], { visibility: 'visible', opacity: 1 });
        document.querySelectorAll('[data-split], [data-fit]').forEach((el) => whenNear(el, fitLongestWord));
        return;
    }

    // Cada pieza se prepara recién cuando está a una pantalla de distancia (dividir títulos en letras y medirlos
    // cuesta): el trabajo se reparte a lo largo del scroll en lugar de trabar la carga.
    document.querySelectorAll('[data-fit]:not([data-split])').forEach((el) => whenNear(el, fitLongestWord));

    // Títulos: cada carácter sube desde su máscara de línea.
    document.querySelectorAll('[data-split]').forEach((el) => whenNear(el, () => {
        fitLongestWord(el);
        const split = SplitText.create(el, { type: 'lines,words,chars', mask: 'lines', linesClass: 'pb-[0.06em]', wordsClass: 'whitespace-nowrap' });
        gsap.set(el, { visibility: 'visible', opacity: 1 });
        gsap.from(split.chars, {
            yPercent: 150, filter: 'blur(6px)', duration: 1.2, ease: 'fluid', stagger: 0.018,
            scrollTrigger: { trigger: el, start: 'top 85%' },
        });
    }));

    // Párrafos grandes: cada palabra se enciende con el degradé de marca a medida que se lee (scrub).
    document.querySelectorAll('[data-words]').forEach((el) => whenNear(el, () => {
        // aria 'none': un <p> no admite aria-label; las palabras siguen leyéndose enteras.
        const split = SplitText.create(el, { type: 'words', aria: 'none' });
        paintWords(el, split.words);
        new ResizeObserver(() => paintWords(el, split.words)).observe(el);
        // Cada palabra pasa de desenfocada y apagada a nítida y con color: "entra en foco".
        // Termina justo cuando el párrafo entra completo: con todo el texto a la vista, todo está encendido.
        const read = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 88%', end: 'bottom bottom', scrub: true } })
            .fromTo(split.words, { opacity: 0.22, filter: 'grayscale(1) brightness(0.6) blur(2px)' }, {
                opacity: 1, filter: 'grayscale(0) brightness(1) blur(0px)', ease: 'none', stagger: 0.1, duration: 0.5,
            }, 0);
        // El resplandor del sol se enciende junto con su palabra.
        el.querySelectorAll('[data-flag]').forEach((flag) => {
            const index = split.words.indexOf(flag.querySelector('div'));
            read.fromTo(flag, { '--glow': 0 }, { '--glow': 1, ease: 'none', duration: 0.5 }, Math.max(index, 0) * 0.1);
        });
    }));

    // Bloques chicos: entrada corta y discreta.
    document.querySelectorAll('[data-reveal]').forEach((el) => whenNear(el, () => {
        gsap.fromTo(el, { autoAlpha: 0, y: 24 }, {
            autoAlpha: 1, y: 0, duration: 1, ease: 'fluid',
            scrollTrigger: { trigger: el, start: 'top 90%' },
        });
    }));

    document.querySelectorAll('[data-reveal-group]').forEach((group) => whenNear(group, () => {
        gsap.fromTo(group.children, { autoAlpha: 0, y: 24 }, {
            autoAlpha: 1, y: 0, duration: 1, ease: 'fluid', stagger: 0.08,
            scrollTrigger: { trigger: group, start: 'top 88%' },
        });
    }));
}

/*
 * Prepara un elemento cuando está a una pantalla de distancia (arriba o abajo), de a uno por momento libre
 * del navegador: ninguna tarea larga, aunque haya muchos elementos cerca al cargar.
 */
const nearQueue = [];
let nearPumping = false;
function whenNear(el, setup) {
    const io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        nearQueue.push(() => setup(el));
        if (!nearPumping) pumpNear();
    }, { rootMargin: '100% 0px 100% 0px' });
    io.observe(el);
}
async function pumpNear() {
    nearPumping = true;
    while (nearQueue.length) {
        await idle();
        try { nearQueue.shift()(); } catch (error) { console.error(error); }
    }
    nearPumping = false;
}

/* ---------- Hero: el mundo se condensa, gira, frena y forma el monograma (sin depender del scroll) ---------- */
function initHero() {
    const hero = document.querySelector('[data-hero]');
    if (!hero) return null;

    const canvas = hero.querySelector('[data-hero-gl]');
    const fallback = hero.querySelector('[data-hero-fallback]');
    const lines = hero.querySelectorAll('[data-hero-line]');
    const lateLines = hero.querySelectorAll('[data-hero-line-late]');
    const intro = hero.querySelector('[data-hero-intro]');
    const hint = hero.querySelector('[data-scroll-hint]');
    // Si la página se recarga scrolleada, el hero se arma ya terminado: nada que esperar al volver arriba.
    const startedBelow = window.scrollY > hero.offsetHeight * 0.4;

    /*
     * Coreografía de entrada (≈2,7 s), todo superpuesto y sin esperas largas:
     * 0.0  sube "Desarrollamos software para que tu empresa"; las partículas llegan y forman el mundo
     * 0.9  el mundo frena y se reordena en el monograma
     * 1.4  sube "no se detenga" mientras el monograma termina de formarse; enseguida la bajada y el CTA
     * La bajada puede arrancar invisible: el elemento grande de la carga (LCP) es el monograma estático, que ya está pintado.
     */
    const enter = gsap.timeline({ paused: true })
        // Suben desde su máscara, sin desenfoque: animar un filtro sobre titulares grandes repinta todo en cada cuadro.
        .from(lines, { yPercent: 160, duration: 1.1, ease: 'fluid', stagger: 0.08 }, 0.05)
        .from(lateLines, { yPercent: 160, duration: 1.1, ease: 'fluid', stagger: 0.1 }, 1.4)
        .from(intro, { autoAlpha: 0, y: 30, duration: 1, ease: 'fluid' }, 1.6)
        // El CTA llega último, con un pequeño impulso, y un único destello lo señala.
        .from(hero.querySelector('[data-hero-cta]'), { autoAlpha: 0, y: 24, scale: 0.92, transformOrigin: '0% 50%', duration: 0.9, ease: 'fluid' }, 1.85)
        .call(() => hero.querySelector('[data-hero-cta] .btn')?.classList.add('is-shining'), null, 2.6)
        .from(hint, { autoAlpha: 0, duration: 1 }, 1.9);
    // Las líneas de la esquina se dibujan durante la entrada; el arco punteado gira muy lento, siempre.
    const draw = hero.querySelectorAll('[data-draw]');
    gsap.set(draw, { strokeDasharray: 1, strokeDashoffset: 1 });
    enter.to(draw, { strokeDashoffset: 0, duration: 1.8, ease: 'fluid', stagger: 0.12 }, 0.2);
    gsap.to(hero.querySelectorAll('[data-spin-slow]'), { rotation: 360, duration: 120, ease: 'none', repeat: -1, transformOrigin: '50% 50%' });
    gsap.fromTo(hero.querySelector('[data-scroll-hint-bar]'), { yPercent: -100 }, { yPercent: 200, duration: 1.6, ease: 'swift', repeat: -1 });

    // Al scrollear, el texto se aleja. Se animan los envoltorios (no los elementos de la entrada): si la página
    // carga scrolleada, la salida no puede quedarse con el estado "invisible" de una entrada que no arrancó.
    gsap.timeline({ scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 } })
        .to(hero.querySelector('h1'), { yPercent: -40, ease: 'none', duration: 1 }, 0)
        .to(hero.querySelectorAll('[data-hero-leave]'), { autoAlpha: 0, y: -40, ease: 'none', duration: 0.35 }, 0);

    if (startedBelow) enter.progress(1);

    // three.js se carga en paralelo: el texto no lo espera, y las partículas se suman cuando llegan.
    loadHeroGL(hero, canvas, fallback, enter, startedBelow);

    // Se devuelve envuelto: un timeline de GSAP es "thenable" y un await esperaría a que termine.
    return { enter };
}

async function loadHeroGL(hero, canvas, fallback, enter, startedBelow) {
    let gl;
    try {
        const { createHeroGL } = await import('./hero-gl.js');
        gl = await createHeroGL(canvas, { logoUrl: fallback.getAttribute('src') });
    } catch (error) {
        // Sin WebGL queda el monograma estático, que ya está en su lugar desde la primera pintura.
        console.warn('WebGL no disponible, se muestra el monograma estático.', error);
        canvas.remove();
        return;
    }

    gsap.ticker.add((time, deltaMs) => gl.tick(time, deltaMs / 1000));
    if (import.meta.env.DEV) window.__heroGL = gl;
    // Se renderiza mientras el hero está a la vista o las partículas siguen visibles.
    // Se decide con los valores animados (no con la posición del scroll), porque el scrub va con retraso:
    // con un scroll rápido, apagar por posición congelaba el viaje a mitad de camino.
    let heroVisible = true;
    new IntersectionObserver(([entry]) => { heroVisible = entry.isIntersecting; }).observe(hero);
    gsap.ticker.add(() => {
        const visible = gl.uniforms.uDockFade.value < 0.999;
        gl.setRunning(heroVisible || visible);
        canvas.style.visibility = visible ? 'visible' : 'hidden';
    });

    // three.js a tiempo (la entrada recién arranca): la historia completa, mundo que gira y forma el monograma.
    // Tarde (celular lento, recarga más abajo, sin movimiento): las partículas aparecen ya formadas y se funden
    // con el monograma estático, sin un cambio brusco segundos después de la carga.
    const onTime = motion() && !startedBelow && enter.time() < 0.3;
    if (onTime) {
        gsap.set(fallback, { autoAlpha: 0 });
        const late = Math.max(0, enter.time());
        gsap.timeline()
            .fromTo(gl.uniforms.uIntro, { value: 0 }, { value: 1, duration: 1.4, ease: 'fluid' }, 0)
            .fromTo(gl.uniforms.uProgress, { value: 0 }, { value: 1, duration: 1.8, ease: 'swift' }, Math.max(0, 0.9 - late));
    } else {
        gl.uniforms.uIntro.value = 1;
        gl.uniforms.uProgress.value = 1;
        gsap.to(fallback, { autoAlpha: 0, duration: motion() ? 0.8 : 0, ease: 'fluid' });
    }
    if (motion()) initDock(hero, canvas, gl);
}

/* ---------- Viaje de las partículas del hero al monograma de la cabecera ---------- */
function initDock(hero, canvas, gl) {
    const dockTarget = document.querySelector('[data-dock-target]');
    if (!dockTarget) return;

    // El canvas pasa a fijo para que el flujo pueda llegar a la cabecera mientras la página sube.
    canvas.classList.replace('absolute', 'fixed');
    // El destino es el logo de la cabecera en su lugar de reposo: si la cabecera está escondida o a mitad
    // de su animación, se descuenta su desplazamiento. Si no, el viaje terminaba arriba, fuera de la pantalla.
    const nav = document.querySelector('[data-nav]');
    const placeTarget = () => {
        const rect = dockTarget.getBoundingClientRect();
        const shift = nav ? new DOMMatrixReadOnly(getComputedStyle(nav).transform).m42 : 0;
        gl.setDockTarget({ left: rect.left, top: rect.top - shift, width: rect.width, height: rect.height });
    };
    placeTarget();
    window.addEventListener('resize', placeTarget);
    window.visualViewport?.addEventListener('resize', placeTarget);

    // El scroll dispara el viaje, no lo controla: al bajar un poco (con margen) arranca y corre
    // completo a su ritmo, sin quedar frenado a mitad de camino. Volviendo al tope, regresa.
    gsap.set(dockTarget, { autoAlpha: 0 });
    const flight = gsap.timeline({
        paused: true,
        onStart: () => { docking = true; },
        onComplete: () => { docking = false; },
        onReverseComplete: () => { docking = false; },
    })
        // Arranca con impulso (sin rampa lenta): el primer scroll ya se nota.
        .fromTo(gl.uniforms.uDock, { value: 0 }, { value: 1, ease: 'power1.out', duration: 3.4 }, 0)
        // Relevo corto: las partículas se apagan casi enteras antes de que aparezca el logo real,
        // así nunca se ven dos monogramas a la vez.
        .fromTo(gl.uniforms.uDockFade, { value: 0 }, { value: 1, ease: 'none', duration: 0.35 }, 3.25)
        .fromTo(dockTarget, { autoAlpha: 0 }, { autoAlpha: 1, ease: 'fluid', duration: 0.4 }, 3.45);

    // Si la página ya está scrolleada (recarga), el monograma está en la cabecera desde el principio.
    if (window.scrollY > 30) flight.progress(1);

    const go = () => {
        if (flight.progress() === 1 && !flight.reversed()) return;
        docking = true;
        // La cabecera tiene que estar a la vista para recibir el logo; se mide justo antes de salir.
        if (nav) { gsap.killTweensOf(nav); gsap.set(nav, { clearProps: 'transform' }); }
        placeTarget();
        flight.timeScale(1).play();
    };
    const back = () => { docking = true; flight.timeScale(1.6).reverse(); };
    // Margen chico: arranca a los ~30 px y solo vuelve cerca del tope (sin rebotes en el umbral).
    ScrollTrigger.create({ trigger: hero, start: 'top+=30 top', onEnter: go });
    // Con scroll suave, se dispara por la intención (destino del scroll), sin esperar a que la página llegue.
    lenis?.on('scroll', ({ targetScroll }) => {
        if (targetScroll > 30 && !docking && flight.progress() === 0) go();
    });
    ScrollTrigger.create({ trigger: hero, start: 'top+=6 top', onLeaveBack: back });
}

/* ---------- Servicios: un solo servicio en foco bajo el cursor ---------- */
function initServices() {
    const section = document.querySelector('[data-services]');
    if (!section || !motion() || !finePointer()) return;

    const rows = gsap.utils.toArray(section.querySelectorAll('[data-service]'));
    rows.forEach((row, i) => {
        row.querySelector('button').addEventListener('pointerenter', () => {
            rows.forEach((other, j) => gsap.to(other, { opacity: j === i ? 1 : 0.28, duration: 0.5, ease: 'fluid', overwrite: 'auto' }));
        });
    });
    section.querySelector('ol').addEventListener('pointerleave', () => {
        gsap.to(rows, { opacity: 1, duration: 0.5, ease: 'fluid', overwrite: 'auto' });
    });
}

/* ---------- Proyectos y productos: el plano se descubre con el scroll; el dial dibuja la jornada ---------- */
function initFeatured() {
    if (!motion()) return;

    document.querySelectorAll('[data-showcase-plane]').forEach((plane) => {
        gsap.fromTo(plane, { clipPath: 'inset(18% 8% 0% 8% round 1.25rem)' }, {
            clipPath: 'inset(0% 0% 0% 0% round 1.25rem)', ease: 'none',
            scrollTrigger: { trigger: plane, start: 'top 95%', end: 'top 45%', scrub: 1 },
        });
    });

    document.querySelectorAll('[data-dial]').forEach((dial) => {
        const arc = dial.querySelector('[data-dial-arc]');
        const length = parseFloat(arc.dataset.length);
        const progress = parseFloat(arc.dataset.progress);
        gsap.timeline({ scrollTrigger: { trigger: dial, start: 'top 75%' } })
            .from(dial.querySelectorAll('[data-dial-tick]'), { opacity: 0, duration: 0.6, ease: 'fluid', stagger: { each: 0.012 } }, 0)
            .fromTo(arc, { attr: { 'stroke-dashoffset': length } }, { attr: { 'stroke-dashoffset': length * (1 - progress) }, duration: 2.2, ease: 'fluid' }, 0.3)
            .fromTo(dial.querySelector('[data-dial-hand]'), { rotation: 0, svgOrigin: '200 200' }, { rotation: progress * 360, svgOrigin: '200 200', duration: 2.2, ease: 'fluid' }, 0.3);
    });

    // Dial: terminado el dibujo, el reloj sigue vivo. Un segundero recorre las marcas y los minutos avanzan.
    document.querySelectorAll('[data-dial]').forEach((dial) => {
        const ticks = gsap.utils.toArray(dial.querySelectorAll('[data-dial-tick]'));
        const sweep = gsap.fromTo(ticks, { attr: { 'stroke-opacity': 1 } }, {
            attr: { 'stroke-opacity': (i, el) => el.dataset.base }, duration: 1.4, ease: 'fluid',
            stagger: { each: 0.25, repeat: -1, repeatDelay: ticks.length * 0.25 - 1.4 }, paused: true,
        });
        const time = dial.querySelector('[data-dial-time]');
        let minutes = 8 * 60 + 57;
        const tick = gsap.timeline({ repeat: -1, repeatDelay: 3.4, paused: true })
            .to(time, { yPercent: -110, duration: 0.45, ease: 'swift' })
            .add(() => {
                minutes = (minutes + 1) % (24 * 60);
                time.textContent = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
            })
            // immediateRender: false. Si no, al crearse deja el número debajo de la máscara (invisible) hasta el primer giro.
            .fromTo(time, { yPercent: 110 }, { yPercent: 0, duration: 0.6, ease: 'fluid', immediateRender: false });
        whileVisible(dial, [sweep, tick], 2.6);
    });

    // QR de Time: se dibuja al entrar (visor, patrones, puntos) y después se renueva en bucle: la línea de
    // vencimiento se acorta, una lectura lo recorre y debajo de ella queda el código nuevo.
    document.querySelectorAll('[data-qr]').forEach((qr) => {
        const cells = JSON.parse(qr.dataset.qrCells);
        const eyes = qr.querySelectorAll('[data-qr-eye]');
        const scan = qr.querySelector('[data-qr-scan]');
        const count = qr.querySelector('[data-qr-count]');
        const oldDots = qr.querySelector('[data-qr-old]');
        const newDots = qr.querySelector('[data-qr-new]');
        const wipeNew = qr.querySelector('[data-qr-wipe-new]');
        const wipeOld = qr.querySelector('[data-qr-wipe-old]');
        const seconds = { value: 5 };
        const dots = (on) => cells.filter((_, i) => on[i]).map(([x, y]) => `M${x + 0.24} ${y + 0.5}a.26 .26 0 1 0 .52 0a.26 .26 0 1 0 -.52 0`).join('');
        let state = cells.map(([, , on]) => on);

        gsap.timeline({ scrollTrigger: { trigger: qr, start: 'top 85%' } })
            .fromTo(qr.querySelectorAll('[data-qr-draw]'), { strokeDasharray: 1, strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.2, ease: 'fluid', stagger: 0.08 }, 0)
            .from(eyes, { scale: 0, transformOrigin: '50% 50%', duration: 0.7, ease: 'fluid', stagger: 0.1 }, 0.5)
            .from(qr.querySelector('[data-qr-dots]'), { opacity: 0, scale: 0.92, transformOrigin: '50% 50%', duration: 0.9, ease: 'fluid' }, 0.6);

        // La lectura barre de arriba hacia abajo: arriba de la línea ya está el código nuevo, abajo el anterior.
        const sweep = { y: -3 };
        const placeSweep = () => {
            scan.setAttribute('y1', sweep.y); scan.setAttribute('y2', sweep.y);
            wipeNew.setAttribute('height', Math.max(0, sweep.y + 3));
            wipeOld.setAttribute('y', sweep.y); wipeOld.setAttribute('height', Math.max(0, 24 - sweep.y));
        };
        const loop = gsap.timeline({ repeat: -1, paused: true })
            .fromTo(qr.querySelector('[data-qr-timer]'), { scaleX: 1 }, { scaleX: 0, duration: 5, ease: 'none' }, 0)
            .fromTo(seconds, { value: 5 }, { value: 0, duration: 5, ease: 'none', onUpdate: () => { count.textContent = `${Math.ceil(seconds.value)} s`; } }, 0)
            .add(() => {
                // Código nuevo: cambia cerca de un tercio de los puntos.
                state = state.map((on) => (Math.random() < 0.35 ? !on : on));
                newDots.setAttribute('d', dots(state));
            }, 3.5)
            .fromTo(scan, { opacity: 0 }, { opacity: 1, duration: 0.15 }, 3.6)
            .fromTo(sweep, { y: -3 }, { y: 24, duration: 1.1, ease: 'swift', onUpdate: placeSweep }, 3.6)
            .to(scan, { opacity: 0, duration: 0.25 }, 4.6)
            .to(eyes, { scale: 1.25, transformOrigin: '50% 50%', duration: 0.25, ease: 'swift', yoyo: true, repeat: 1 }, 4.7)
            .add(() => {
                // El nuevo pasa a ser el actual y las máscaras vuelven a su lugar para la próxima lectura.
                oldDots.setAttribute('d', newDots.getAttribute('d'));
                sweep.y = -3;
                placeSweep();
            }, 4.95);
        whileVisible(qr, [loop], 2);
    });

    // Gastronomy en desarrollo: el llamador de mesa se construye como un plano técnico (guías, nodos, cotas,
    // pieza), llama dos veces y se desarma, en bucle. Dice "todavía se está construyendo".
    document.querySelectorAll('[data-bell]').forEach((bell) => {
        const q = (sel) => gsap.utils.toArray(bell.querySelectorAll(sel));
        const guides = q('[data-bell-guide]');
        const nodes = q('[data-bell-node]');
        const labels = q('[data-bell-label]');
        const parts = q('[data-bell-draw]');
        const button = bell.querySelector('[data-bell-button]');
        const fill = bell.querySelector('[data-bell-fill]');
        const waves = q('[data-bell-wave]');
        const apex = nodes[nodes.length - 1];
        const origin = `${apex.getAttribute('cx')} ${Number(apex.getAttribute('cy')) - 4}`;

        // Opacidad junto con el trazo: un trazo de largo cero deja ver la punta redondeada como un punto.
        gsap.set(parts, { strokeDasharray: 1, strokeDashoffset: 1, opacity: 0 });
        gsap.set(guides, { opacity: 0, scale: 0.6, transformOrigin: '50% 50%' });
        gsap.set([...nodes, ...labels], { opacity: 0 });
        gsap.set(fill, { fillOpacity: 0 });
        gsap.set(waves, { opacity: 0, scale: 0.7, svgOrigin: origin });

        const call = () => gsap.timeline()
            .to(button, { y: 2, duration: 0.12, ease: 'swift', yoyo: true, repeat: 1 })
            .fromTo(waves, { opacity: 0, scale: 0.7 }, { opacity: 1, scale: 1, svgOrigin: origin, duration: 0.5, ease: 'fluid', stagger: 0.12 }, 0.1)
            .to(waves, { opacity: 0, duration: 0.5, ease: 'fluid', stagger: 0.12 }, 0.55);

        const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.8, paused: true })
            .to(guides, { opacity: 1, scale: 1, duration: 0.8, ease: 'fluid', stagger: 0.12 }, 0)
            .fromTo(nodes, { opacity: 0, scale: 0, transformOrigin: '50% 50%' }, { opacity: 1, scale: 1, duration: 0.4, ease: 'fluid', stagger: 0.08 }, 0.5)
            .fromTo(parts, { strokeDashoffset: 1, opacity: 0 }, { strokeDashoffset: 0, opacity: 1, duration: 1.1, ease: 'fluid', stagger: 0.18 }, 0.8)
            .to(labels, { opacity: 1, duration: 0.5, ease: 'fluid', stagger: 0.15 }, 1.6)
            .to(fill, { fillOpacity: 0.85, duration: 0.6, ease: 'fluid' }, 2.4)
            .add(call(), 2.9)
            .add(call(), 3.9)
            // Se desarma en el orden inverso y vuelve a empezar.
            .to(fill, { fillOpacity: 0, duration: 0.4, ease: 'fluid' }, 5.3)
            .to([...parts].reverse(), { strokeDashoffset: -1, opacity: 0, duration: 0.7, ease: 'swift', stagger: 0.08 }, 5.4)
            .to([...labels, ...nodes], { opacity: 0, duration: 0.4, ease: 'fluid' }, 5.6)
            .to(guides, { opacity: 0, scale: 0.6, duration: 0.6, ease: 'swift', stagger: 0.06 }, 6);
        whileVisible(bell, [loop], 0.3);
    });

    // Proyecto: el logo sigue al puntero con inercia, apenas.
    if (finePointer()) {
        document.querySelectorAll('[data-showcase-plane]').forEach((plane) => {
            const logo = plane.querySelector('[data-showcase-float]');
            if (!logo) return;
            const x = gsap.quickTo(logo, 'x', { duration: 1.2, ease: 'fluid' });
            const y = gsap.quickTo(logo, 'y', { duration: 1.2, ease: 'fluid' });
            plane.addEventListener('pointermove', (e) => {
                const r = plane.getBoundingClientRect();
                x(((e.clientX - r.left) / r.width - 0.5) * 36);
                y(((e.clientY - r.top) / r.height - 0.5) * 24);
            });
            plane.addEventListener('pointerleave', () => { x(0); y(0); });
        });
    }
}

// Corre las animaciones en bucle solo mientras el elemento está a la vista (la primera vez, después de su entrada).
function whileVisible(el, loops, firstDelay = 0) {
    let started = false;
    ScrollTrigger.create({
        trigger: el, start: 'top bottom', end: 'bottom top',
        onToggle: ({ isActive }) => {
            // Al salir vuelven a su inicio: pausadas a mitad (la hora del reloj en pleno giro) quedaban a medio mostrar.
            if (!isActive) return loops.forEach((l) => l.pause(0));
            if (started) return loops.forEach((l) => l.play());
            started = true;
            gsap.delayedCall(firstDelay, () => loops.forEach((l) => l.play()));
        },
    });
}

/* ---------- Rótulos y líneas: el número de sección cuenta hasta el suyo; las líneas de los pasos se trazan ---------- */
function initLabels() {
    if (!motion()) return;
    document.querySelectorAll('[data-label-number]').forEach((el) => {
        const target = Number(el.textContent);
        const counter = { value: 0 };
        gsap.to(counter, {
            value: target, duration: 0.9, ease: 'swift', snap: { value: 1 },
            onUpdate: () => { el.textContent = String(counter.value).padStart(2, '0'); },
            scrollTrigger: { trigger: el, start: 'top 88%' },
        });
    });
    document.querySelectorAll('[data-line-group]').forEach((group) => {
        gsap.from(group.querySelectorAll('[data-line]'), {
            scaleX: 0, transformOrigin: '0% 50%', duration: 1.2, ease: 'fluid', stagger: 0.12,
            scrollTrigger: { trigger: group, start: 'top 85%' },
        });
    });
}

/* ---------- Cierre: tipografía que deriva con el scroll y se inclina con la velocidad ---------- */
function initCta() {
    const cta = document.querySelector('[data-cta]');
    if (!cta) return;
    const lines = cta.querySelectorAll('[data-drift]');

    // La línea más larga tiene que entrar completa en algún momento de la deriva.
    const heading = lines[0].parentElement;
    heading.style.fontSize = '';
    const widest = Math.max(...[...lines].map((line) => line.scrollWidth));
    const available = heading.clientWidth * 0.94;
    if (widest > available) {
        heading.style.fontSize = `${Math.floor(parseFloat(getComputedStyle(heading).fontSize) * (available / widest))}px`;
    }

    if (!motion()) return;

    lines.forEach((line) => {
        const dir = Number(line.dataset.drift);
        gsap.fromTo(line, { xPercent: -7 * dir }, {
            xPercent: 7 * dir, ease: 'none',
            scrollTrigger: { trigger: cta, start: 'top bottom', end: 'bottom top', scrub: true },
        });
    });

    if (lenis) {
        const skews = [...lines].map((line) => gsap.quickTo(line, 'skewX', { duration: 0.6, ease: 'fluid' }));
        lenis.on('scroll', ({ velocity }) => skews.forEach((skew) => skew(gsap.utils.clamp(-8, 8, velocity * -0.35))));
    }
}

/* ---------- Capas decorativas: cada profundidad se mueve a su velocidad con el scroll y el puntero ---------- */
function initDeco() {
    if (!motion()) return;
    const layers = gsap.utils.toArray('[data-depth]');
    layers.forEach((el) => {
        const depth = Number(el.dataset.depth);
        const section = el.closest('section') ?? el.parentElement;
        if (el.hasAttribute('data-center')) gsap.set(el, { xPercent: -50, yPercent: -50 });
        const scroll = { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true };

        gsap.fromTo(el, { y: depth * 220 }, { y: depth * -220, ease: 'none', scrollTrigger: scroll });
        if (el.dataset.spin) gsap.to(el, { rotation: Number(el.dataset.spin) * 120, ease: 'none', scrollTrigger: { ...scroll } });

        // Fuera del hero (que tiene su propia entrada), cada pieza aparece al llegar y se difumina al irse.
        if (el.closest('[data-hero]') || el.hasAttribute('data-deco-clock')) return;
        gsap.timeline({ scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } })
            .fromTo(el, { opacity: 0, filter: 'blur(6px)' }, { opacity: 1, filter: 'blur(0px)', ease: 'none', duration: 0.3 })
            .to(el, { opacity: 1, duration: 0.4 })
            .to(el, { opacity: 0, filter: 'blur(6px)', ease: 'none', duration: 0.3 });
    });

    // Reloj de fondo de Time: entra al bajar, las agujas giran con el scroll y se difumina al dejar la sección.
    const clock = document.querySelector('[data-deco-clock]');
    const band = document.querySelector('[data-time-band]');
    if (clock && band) {
        gsap.timeline({ scrollTrigger: { trigger: band, start: 'top bottom', end: 'bottom top', scrub: 0.6 } })
            .fromTo(clock, { opacity: 0, scale: 0.86, rotation: -24, filter: 'blur(8px)' }, { opacity: 1, scale: 1, rotation: 0, filter: 'blur(0px)', ease: 'none', duration: 0.35 })
            .to(clock, { opacity: 1, duration: 0.3 })
            .to(clock, { opacity: 0, scale: 1.08, filter: 'blur(10px)', ease: 'none', duration: 0.35 })
            .fromTo(clock.querySelector('[data-clock-minute]'), { rotation: 0, svgOrigin: '200 200' }, { rotation: 720, svgOrigin: '200 200', ease: 'none', duration: 1 }, 0)
            .fromTo(clock.querySelector('[data-clock-hour]'), { rotation: 30, svgOrigin: '200 200' }, { rotation: 90, svgOrigin: '200 200', ease: 'none', duration: 1 }, 0);
    }

    if (!finePointer()) return;
    // El puntero desplaza apenas cada capa según su profundidad: paralaje con el mouse.
    const shifts = layers.map((el) => ({
        depth: Number(el.dataset.depth),
        x: gsap.quickTo(el, 'x', { duration: 1.2, ease: 'fluid' }),
    }));
    window.addEventListener('pointermove', (e) => {
        const offset = e.clientX / window.innerWidth - 0.5;
        shifts.forEach(({ depth, x }) => x(offset * depth * -60));
    }, { passive: true });
}

/* ---------- Cursores fantasma: solo escritorio, con movimiento y si hay servidor configurado ---------- */
async function initGhosts() {
    const url = document.querySelector('meta[name="rbsis-cursors"]')?.content;
    if (!url || !motion() || !finePointer() || !('WebSocket' in window)) return;
    try {
        const { initGhostCursors } = await import('./ghost-cursors.js');
        initGhostCursors(url);
    } catch (error) {
        console.warn('Cursores fantasma no disponibles.', error);
    }
}

/* ---------- Pie: el logotipo emerge al llegar al final ---------- */
function initFooter() {
    const mark = document.querySelector('[data-footer-mark]');
    if (!mark || !motion()) return;
    gsap.fromTo(mark, { yPercent: 70 }, {
        yPercent: 0, ease: 'none',
        scrollTrigger: { trigger: mark.parentElement, start: 'top bottom', end: 'bottom bottom', scrub: true },
    });
}

/* ---------- Alpine (menú y acordeón). Las páginas con componentes de Livewire ya lo traen con Livewire ---------- */
function initAlpine() {
    if (window.Livewire || document.querySelector('[wire\\:snapshot]')) return;
    Promise.all([import('alpinejs'), import('@alpinejs/collapse')]).then(([{ default: Alpine }, { default: collapse }]) => {
        Alpine.plugin(collapse);
        window.Alpine = Alpine;
        Alpine.start();
    });
}

document.addEventListener('DOMContentLoaded', async () => {
    initAlpine();
    initSmoothScroll();
    initPageTransitions();
    initNav();
    initMobileMenu();
    initMagnetic();

    let heroEnter = null;
    try {
        // Esperar las fuentes para que SplitText mida bien las líneas.
        await document.fonts.ready;
        // Primero lo que se ve al entrar: el hero. Abre el telón y arranca sin esperar al resto de la página.
        heroEnter = initHero()?.enter ?? null;
    } catch (error) {
        console.error(error);
    }

    // Pase lo que pase, el telón se abre.
    window.__rbsisReady = true;
    // El hero arranca mientras el telón todavía se abre: no hay una pausa muerta entre uno y otro.
    const curtain = openCurtain();
    gsap.delayedCall(motion() ? 0.45 : 0, () => heroEnter?.play());

    // El resto de la página se prepara por tandas en los momentos libres del navegador: así no hay
    // tareas largas que traben la entrada (y PageSpeed no lo cuenta como bloqueo).
    try {
        await idle();
        initReveals();
        await idle();
        initServices();
        initFeatured();
        initLabels();
        await idle();
        initCta();
        initFooter();
        initDeco();
        ScrollTrigger.refresh();
    } catch (error) {
        console.error(error);
        gsap.set(['[data-split]', '[data-reveal]', '[data-reveal-group] > *'], { visibility: 'visible', opacity: 1 });
    }

    await curtain;
    initGhosts();
});

// Cede el hilo principal hasta un momento libre (con tope, para no demorar lo que está apenas más abajo).
function idle() {
    return new Promise((resolve) => (window.requestIdleCallback ? requestIdleCallback(() => resolve(), { timeout: 400 }) : setTimeout(resolve, 16)));
}
